import {motion} from 'framer-motion'
import {DataTable} from 'primereact/datatable'
import {Dialog} from 'primereact/dialog'
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
} from 'react'

import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import moment from 'moment-timezone'
import {Button} from 'primereact/button'
import {Column} from 'primereact/column'
import {Tag} from 'primereact/tag'
import {TreeTable} from 'primereact/treetable'
import {SystemRole} from '../../Constants'
import {useAppConfig} from '../../context/app/AppContext'
import {useUserHospital} from '../../context/hospital/UserHospitalContext'
import TranslateUtils from '../../i18n/TranslateUtils'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {QueryKeyManager} from '../../query-manager/QueryKeysManager'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {useAuth} from '../authentication/AuthProvider'
import PaginatorTemplate from '../new-table/PaginatorTemplate'
import {genericLogQueryType} from '../styled-component/generic-log/GenericLogConstansts'
import {convertKeyToLabel} from '../table-utils/TableUtils'
import ExportForm from '../table-utils/exportForm/ExportForm'
import {
    batterChargeLogColumns,
    biometricsColumns,
    dischargeColumns,
    exportColumns,
    hcpRoleColumns,
    importColumns,
    onWristColumns,
    parameterLogColumns,
    userLogColumns,
    watchConfigColumns,
    watchNotificationColumns,
} from './GenericLogConstant'
import {
    genericLogEvent,
    genericLogEventTypes,
    getGenericLogEventKey,
    onGenericLogTableChangeRemoveListener,
} from './broadcast/GenericLogEvent'
import {usePageGenericLogs} from './query-keys/GenericLogKeysManager'

const initialPageable = {
    pageRows: 10,
    pageNumber: 0,
    sortField: 'id',
    sortOrder: -1,
}

const GenericLogTable = forwardRef((props, ref) => {
    useSignals()
    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))

    //#region Context
    const {timezoneId} = useAppConfig()
    const {currentHospital} = useUserHospital()
    const {user} = useAuth()
    const {userRole} = useAppConfig()
    //#endregion

    //#region Translate
    const {translateKeys, translateDatatable, translateCommons} =
        TranslateUtils()
    //#endregion

    //#region State
    const visible = useSignal(false)
    const filterRequest = useSignal(null)
    const toolboxState = useSignal(false)
    const pageable = useSignal({...initialPageable})
    const type = useSignal(genericLogQueryType.none)

    //#endregion

    //#region Handle Function
    const handleShowForm = data => {
        blockOverflownScroll()

        filterRequest.value = {
            refId:
                data?.refId &&
                data?.type !== 'importLog' &&
                data?.type !== 'exportLog'
                    ? data?.refId
                    : null,
            hospitalId:
                userRole === SystemRole.USER_WITH_ROLE
                    ? currentHospital?.id
                    : null,
            refIds: data?.refIds,
            type: data?.type,
            importExportLogType: !!data?.fileType ? data.fileType : null,
        }

        type.value = data?.type || genericLogQueryType.none
        toolboxState.value = data?.toolbox
        visible.value = true
    }

    const handleHideForm = () => {
        unblockOverflownScroll()
        visible.value = false
    }
    //#endregion

    //#region Query
    const queryClient = useQueryClient()
    const {data: genericLogData, refetch} = usePageGenericLogs(
        user?.id,
        type.value,
        pageable.value.pageNumber,
        pageable.value.pageRows,
        pageable.value.sortField,
        pageable.value.sortOrder,
        {...filterRequest.value},
        !!user?.id && !!type.value && !!visible.value,
    )

    //#endregion

    //#region Event Listener

    //#region Event Listener on State Changes
    useEffect(() => {
        genericLogEvent.on(
            `${getGenericLogEventKey(
                genericLogEventTypes.TABLE_CHANGE,
                type.value,
            )}`,
            newData => {
                if (!newData || !newData?.id) {
                    return
                }

                const {genericLogKeys} = QueryKeyManager(
                    QueryKeyTypes.genericLog,
                    user?.id,
                )
                queryClient.invalidateQueries({
                    queryKey: genericLogKeys.pattern({
                        type: type.value,
                    }),
                })
            },
        )

        return () => {
            onGenericLogTableChangeRemoveListener(
                genericLogEventTypes.TABLE_CHANGE,
            )
        }
    }, [type.value])
    //#endregion

    //#region Memo
    const exportCol = useMemo(() => {
        if (filterRequest.value?.type === 'userLog') {
            return userLogColumns
        } else if (filterRequest.value?.type === 'watchConfigLog') {
            return watchConfigColumns
        } else if (filterRequest.value?.type === 'parameterLog') {
            return parameterLogColumns
        } else if (filterRequest.value?.type === 'hcpRoleLog') {
            return hcpRoleColumns
        } else if (filterRequest.value?.type === 'watchNotificationLog') {
            return watchNotificationColumns
        } else if (filterRequest.value?.type === 'biometricsLog') {
            return biometricsColumns
        } else if (filterRequest.value?.type === 'dischargeLog') {
            return dischargeColumns
        } else if (filterRequest.value?.type === 'importLog') {
            return importColumns
        } else if (filterRequest.value?.type === 'exportLog') {
            return exportColumns
        } else if (filterRequest.value?.type === 'onWristLog') {
            return onWristColumns
        } else if (filterRequest.value?.type === 'batteryChargeLog') {
            return batterChargeLogColumns
        }
    }, [filterRequest.value?.type])
    //#endregion

    //#region Data Converting
    const formatMilliseconds = milliseconds => {
        const totalSeconds = Math.floor(milliseconds / 1000)
        const seconds = totalSeconds % 60
        const totalMinutes = Math.floor(totalSeconds / 60)
        const minutes = totalMinutes % 60
        const hours = Math.floor(totalMinutes / 60)

        let result = ''

        if (hours > 0) {
            result += `${hours} hour${hours > 1 ? 's' : ''} `
        }

        if (minutes > 0) {
            result += `${minutes} minute${minutes > 1 ? 's' : ''} `
        }

        if (seconds > 0 || result === '') {
            result += `${seconds} second${seconds > 1 ? 's' : ''} `
        }

        return result.trim()
    }

    const calculateTotalDuration = item => {
        let dataList = []
        if (filterRequest.value?.type === 'onWristLog') {
            if (!item?.onWristLogs || item?.onWristLogs.length === 0) {
                return '-'
            } else {
                dataList = item?.onWristLogs ? [...item?.onWristLogs] : []
            }
        }

        if (filterRequest.value?.type === 'batteryChargeLog') {
            if (
                filterRequest.value?.type === 'batteryChargeLog' &&
                (!item?.batteryChargeLogs ||
                    item?.batteryChargeLogs.length === 0)
            ) {
                return '-'
            } else {
                dataList = item?.batteryChargeLogs
                    ? [...item?.batteryChargeLogs]
                    : []
            }
        }

        const totalDuration = dataList.reduce((acc, log) => {
            if (
                filterRequest.value?.type === 'onWristLog' &&
                log?.duration &&
                typeof log?.duration === 'number'
            ) {
                return acc + log.duration
            }

            if (
                filterRequest.value?.type === 'batteryChargeLog' &&
                log?.chargeDuration &&
                typeof log?.chargeDuration === 'number'
            ) {
                return acc + log.chargeDuration
            }

            return acc
        }, 0)

        return formatMilliseconds(totalDuration)
    }

    const convertDateStringToDateTimezone = dateString => {
        if (!dateString) {
            return ''
        }

        return moment(dateString).tz(timezoneId).format('YYYY-MM-DD HH:mm:ss')
    }

    const convertToDataItem = (viewType, itemType, data, index) => {
        if (
            (viewType !== 'onWristLog' && viewType !== 'batteryChargeLog') ||
            !data ||
            Object.keys(data).length === 0 ||
            !itemType
        ) {
            return {}
        }

        if (viewType === 'onWristLog') {
            if (itemType === 'parentItem') {
                return {
                    from: (
                        <span className="font-bold">
                            {data?.onWristLogs[0]?.from
                                ? convertDateStringToDateTimezone(
                                      data?.onWristLogs[0]?.from,
                                  )
                                : '-'}
                        </span>
                    ),
                    to: (
                        <span className="font-bold">
                            {data?.onWristLogs[data?.onWristLogs.length - 1]?.to
                                ? convertDateStringToDateTimezone(
                                      data?.onWristLogs[
                                          data?.onWristLogs.length - 1
                                      ]?.to,
                                  )
                                : '-'}
                        </span>
                    ),
                    duration: (
                        <span className="font-bold">
                            {calculateTotalDuration(data)}
                        </span>
                    ),
                }
            } else {
                return {
                    from: data?.from
                        ? convertDateStringToDateTimezone(data.from)
                        : '-',
                    to: data?.to
                        ? convertDateStringToDateTimezone(data?.to)
                        : '-',
                    durationInMilliseconds: data?.duration,
                    duration: data?.duration
                        ? formatMilliseconds(data?.duration)
                        : '-',
                }
            }
        }

        if (viewType === 'batteryChargeLog') {
            if (itemType === 'parentItem') {
                return {
                    from: (
                        <span className="font-bold">
                            {data?.batteryChargeLogs[0]?.from
                                ? convertDateStringToDateTimezone(
                                      data?.batteryChargeLogs[0]?.from,
                                  )
                                : '-'}
                        </span>
                    ),
                    to: (
                        <span className="font-bold">
                            {data?.batteryChargeLogs[
                                data?.batteryChargeLogs.length - 1
                            ]?.to
                                ? convertDateStringToDateTimezone(
                                      data?.batteryChargeLogs[
                                          data?.batteryChargeLogs.length - 1
                                      ]?.to,
                                  )
                                : '-'}
                        </span>
                    ),
                    duration: (
                        <span className="font-bold">
                            {calculateTotalDuration(data) +
                                `${
                                    data?.batteryChargeLogs?.length > 0
                                        ? ` | (${data.batteryChargeLogs?.length} times)`
                                        : ''
                                }`}
                        </span>
                    ),
                }
            } else {
                return {
                    from: data?.from
                        ? `${convertDateStringToDateTimezone(data.from)} ${
                              data?.batteryAtCharge
                                  ? `(${data.batteryAtCharge}%)`
                                  : ''
                          }`
                        : '-',
                    to: data?.to
                        ? `${convertDateStringToDateTimezone(data.to)} ${
                              data?.batteryAtDischarge
                                  ? `(${data.batteryAtDischarge}%)`
                                  : ''
                          }`
                        : '-',
                    duration:
                        index === 0 && !data?.to
                            ? 'Still charging'
                            : data?.chargeDuration
                            ? formatMilliseconds(data?.chargeDuration)
                            : '-',
                    chargeDurationInMilliseconds: data?.chargeDuration,
                    percentage: data?.batteryAmount
                        ? `${data.batteryAmount}%`
                        : '-',
                }
            }
        }

        return {}
    }

    const treeData = useMemo(() => {
        if (
            (filterRequest.value?.type !== 'onWristLog' &&
                filterRequest.value?.type !== 'batteryChargeLog') ||
            !genericLogData?.content
        ) {
            return []
        }
        let treeData = []

        genericLogData?.content.forEach(item => {
            const groupByDate = item?.groupByDate

            let logList = []
            if (
                filterRequest.value?.type === 'onWristLog' &&
                item?.onWristLogs?.length > 0
            ) {
                logList = !!item?.onWristLogs ? [...item?.onWristLogs] : []
            }

            if (
                filterRequest.value?.type === 'batteryChargeLog' &&
                item?.batteryChargeLogs?.length > 0
            ) {
                logList = !!item?.batteryChargeLogs
                    ? [...item?.batteryChargeLogs]
                    : []
            }

            logList.reverse()

            const existingNode = treeData.find(
                node => node.groupByDate === groupByDate,
            )
            if (existingNode) {
                existingNode.children.push({
                    ...item,
                    groupByDate,
                })
            } else {
                treeData.push({
                    key: groupByDate,
                    label: groupByDate,
                    data: {
                        groupByDate,
                        ...convertToDataItem(
                            filterRequest.value?.type,
                            'parentItem',
                            item,
                        ),
                    },
                    children: logList?.map((log, idx) => {
                        const dataItem = convertToDataItem(
                            filterRequest.value?.type,
                            'childItem',
                            log,
                            idx,
                        )

                        return {
                            key: `${groupByDate}-${idx}`,
                            label: `${groupByDate}-${idx}`,
                            data: {
                                groupByDate: '-',
                                ...dataItem,
                            },
                        }
                    }),
                })
            }
        })

        return treeData
    }, [filterRequest.value?.type, genericLogData?.content])
    //#endregion

    //#region Render
    const renderColumns = useCallback(() => {
        let shownColumns = []

        if (filterRequest.value?.type === 'userLog') {
            shownColumns = userLogColumns
        } else if (filterRequest.value?.type === 'watchConfigLog') {
            shownColumns = watchConfigColumns
        } else if (filterRequest.value?.type === 'parameterLog') {
            shownColumns = parameterLogColumns
        } else if (filterRequest.value?.type === 'hcpRoleLog') {
            shownColumns = hcpRoleColumns
        } else if (filterRequest.value?.type === 'watchNotificationLog') {
            shownColumns = watchNotificationColumns
        } else if (filterRequest.value?.type === 'biometricsLog') {
            shownColumns = biometricsColumns
        } else if (filterRequest.value?.type === 'dischargeLog') {
            shownColumns = dischargeColumns
        } else if (filterRequest.value?.type === 'importLog') {
            shownColumns = importColumns
        } else if (filterRequest.value?.type === 'exportLog') {
            shownColumns = exportColumns
        } else if (filterRequest.value?.type === 'onWristLog') {
            shownColumns = onWristColumns
        } else if (filterRequest.value?.type === 'batteryChargeLog') {
            shownColumns = batterChargeLogColumns
        }

        return (
            shownColumns?.length > 0 &&
            shownColumns?.map((item, idx) => {
                return (
                    <Column
                        key={item.key}
                        columnKey={item.key}
                        className="overflow-dot overflow-text-2"
                        style={{minWidth: '150px'}}
                        field={item.key}
                        body={rowData =>
                            rowData ? (
                                item.key === 'createdAt' ||
                                item.key === 'modifiedBy' ? (
                                    <div
                                        className="overflow-dot overflow-text-2"
                                        style={{width: '100%'}}>
                                        {rowData[item.key] &&
                                        rowData[item.key] !== ' '
                                            ? rowData[item.key]
                                            : '-'}
                                    </div>
                                ) : filterRequest.value?.type === 'userLog' ? (
                                    <div
                                        className="overflow-dot overflow-text-2"
                                        style={{width: '100%'}}>
                                        {rowData?.userLog &&
                                            (item.key === 'active' ||
                                            item.key === 'deviceAssigned' ||
                                            item.key === 'deleted' ? (
                                                !!rowData.userLog[item.key] ? (
                                                    <Tag
                                                        className="px-3"
                                                        severity="success"
                                                        value={translateDatatable(
                                                            'Yes',
                                                        )}
                                                    />
                                                ) : (
                                                    <Tag
                                                        severity="danger"
                                                        value={translateDatatable(
                                                            'No',
                                                        )}
                                                        className="px-3"
                                                    />
                                                )
                                            ) : rowData?.userLog[item.key] ? (
                                                rowData?.userLog[item.key]
                                            ) : (
                                                '-'
                                            ))}
                                    </div>
                                ) : filterRequest.value?.type ===
                                  'watchNotificationLog' ? (
                                    item.key === 'image' ? (
                                        rowData?.watchNotificationLog &&
                                        rowData?.watchNotificationLog[
                                            item.key
                                        ] ? (
                                            <img
                                                src={
                                                    rowData
                                                        ?.watchNotificationLog[
                                                        item.key
                                                    ]
                                                }
                                                alt="watch-config"
                                                style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    borderRadius: '8px',
                                                }}
                                            />
                                        ) : (
                                            '-'
                                        )
                                    ) : (
                                        <div
                                            className="overflow-dot overflow-text-2"
                                            style={{width: '100%'}}>
                                            {rowData?.watchNotificationLog &&
                                            rowData?.watchNotificationLog[
                                                item.key
                                            ]
                                                ? rowData?.watchNotificationLog[
                                                      item.key
                                                  ]
                                                : '-'}
                                        </div>
                                    )
                                ) : filterRequest.value?.type ===
                                  'hcpRoleLog' ? (
                                    item.key === 'roleName' ? (
                                        <div
                                            className="overflow-dot overflow-text-2"
                                            style={{width: '100%'}}>
                                            {rowData?.hcpRoleLog?.role['name']
                                                ? rowData?.hcpRoleLog?.role[
                                                      'name'
                                                  ]
                                                : '-'}
                                        </div>
                                    ) : item.key === 'hospitalName' ? (
                                        <div
                                            className="overflow-dot overflow-text-2"
                                            style={{width: '100%'}}>
                                            {rowData?.hcpRoleLog?.hospital[
                                                'name'
                                            ]
                                                ? rowData?.hcpRoleLog?.hospital[
                                                      'name'
                                                  ]
                                                : '-'}
                                        </div>
                                    ) : (
                                        <div
                                            className="overflow-dot overflow-text-2"
                                            style={{width: '100%'}}>
                                            {rowData?.hcpRoleLog &&
                                            rowData?.hcpRoleLog[item.key]
                                                ? rowData?.hcpRoleLog[item.key]
                                                : '-'}
                                        </div>
                                    )
                                ) : filterRequest.value?.type ===
                                  'importLog' ? (
                                    // <div
                                    //     className="overflow-dot overflow-text-2"
                                    //     style={{width: '100%'}}>
                                    //     <Button
                                    //         className="p-button-rounded "
                                    //         icon="pi pi-pencil"
                                    //         aria-label="Edit"
                                    //         tooltip="Edit"
                                    //         onClick={() => {}}
                                    //         tooltipOptions={{
                                    //             position: 'top',
                                    //         }}
                                    //     />
                                    // </div>
                                    <div
                                        className="overflow-dot overflow-text-2"
                                        style={{width: '100%'}}>
                                        {rowData?.importLog &&
                                        rowData?.importLog[item.key]
                                            ? rowData?.importLog[item.key]
                                            : '-'}
                                    </div>
                                ) : filterRequest.value?.type ===
                                  'exportLog' ? (
                                    <div
                                        className="overflow-dot overflow-text-2"
                                        style={{width: '100%'}}>
                                        {rowData?.exportLog &&
                                        rowData?.exportLog[item.key]
                                            ? rowData?.exportLog[item.key]
                                            : '-'}
                                    </div>
                                ) : filterRequest.value?.type ===
                                  'watchConfigLog' ? (
                                    <div
                                        className="overflow-dot overflow-text-2"
                                        style={{width: '100%'}}>
                                        {rowData?.watchConfigLog &&
                                        rowData?.watchConfigLog[item.key]
                                            ? rowData?.watchConfigLog[item.key]
                                            : '-'}
                                    </div>
                                ) : filterRequest.value?.type ===
                                  'parameterLog' ? (
                                    <div
                                        className="overflow-dot overflow-text-2"
                                        style={{width: '100%'}}>
                                        {rowData?.parameterLog &&
                                        rowData?.parameterLog[item.key]
                                            ? rowData?.parameterLog[item.key]
                                            : '-'}
                                    </div>
                                ) : filterRequest.value?.type ===
                                  'dischargeLog' ? (
                                    <div
                                        className="overflow-dot overflow-text-2"
                                        style={{width: '100%'}}>
                                        {rowData?.dischargeLog &&
                                        rowData?.dischargeLog[item.key]
                                            ? rowData?.dischargeLog[item.key]
                                            : '-'}
                                    </div>
                                ) : filterRequest.value?.type ===
                                  'biometricsLog' ? (
                                    <div
                                        className="overflow-dot overflow-text-2"
                                        style={{width: '100%'}}>
                                        {rowData?.biometricsLog &&
                                        rowData?.biometricsLog[item.key]
                                            ? item.key === 'height'
                                                ? `${
                                                      rowData?.biometricsLog[
                                                          item.key
                                                      ]
                                                  }
                                                     ${
                                                         rowData?.biometricsLog[
                                                             'type'
                                                         ] === 'inchPound'
                                                             ? ` ${translateKeys(
                                                                   'In',
                                                               )}`
                                                             : ` ${translateKeys(
                                                                   'Kg',
                                                               )}`
                                                     }`
                                                : item.key === 'weight'
                                                ? `${
                                                      rowData?.biometricsLog[
                                                          item.key
                                                      ]
                                                  }
                                                      ${
                                                          rowData
                                                              ?.biometricsLog[
                                                              'type'
                                                          ] === 'inchPound'
                                                              ? ` ${translateKeys(
                                                                    'lbs',
                                                                )}`
                                                              : ` ${translateKeys(
                                                                    'Kg',
                                                                )}`
                                                      }`
                                                : rowData?.biometricsLog[
                                                      item.key
                                                  ]
                                            : '-'}
                                    </div>
                                ) : filterRequest.value?.type ===
                                  'dischargeLog' ? (
                                    <div
                                        className="overflow-dot overflow-text-2"
                                        style={{width: '100%'}}>
                                        {rowData?.dischargeLog &&
                                        rowData?.dischargeLog[item.key]
                                            ? rowData?.dischargeLog[item.key]
                                            : '-'}
                                    </div>
                                ) : (
                                    <div
                                        className="overflow-dot overflow-text-2"
                                        style={{width: '100%'}}>
                                        {rowData[item.key]
                                            ? rowData[item.key]
                                            : '-'}
                                    </div>
                                )
                            ) : (
                                '-'
                            )
                        }
                        header={translateDatatable(
                            convertKeyToLabel(
                                item.key === 'deleted'
                                    ? 'discharged'
                                    : item.key,
                            ),
                        )}
                    />
                )
            })
        )
    }, [filterRequest.value?.type, translateDatatable, translateKeys])

    const renderTable = () => {
        return (
            <React.Fragment>
                {filterRequest.value?.type === 'onWristLog' ||
                filterRequest.value?.type === 'batteryChargeLog' ? (
                    <TreeTable
                        scrollHeight="auto"
                        emptyMessage={translateCommons('No results found')}
                        value={treeData}
                        lazy
                        columnResizeMode="fit"
                        scrollable>
                        <Column
                            header={translateCommons('Date')}
                            body={rowData => {
                                return (
                                    <span className="font-bold">
                                        {rowData?.data?.groupByDate}
                                    </span>
                                )
                            }}
                            expander></Column>
                        {filterRequest.value?.type === 'onWristLog' &&
                            onWristColumns.map((item, idx) => (
                                <Column
                                    key={item.key}
                                    field={item.key}
                                    header={translateCommons(
                                        convertKeyToLabel(item.key),
                                    )}
                                />
                            ))}
                        {filterRequest.value?.type === 'batteryChargeLog' &&
                            batterChargeLogColumns.map((item, idx) => (
                                <Column
                                    key={item.key}
                                    field={item.key}
                                    header={translateCommons(
                                        convertKeyToLabel(item.key),
                                    )}
                                />
                            ))}
                    </TreeTable>
                ) : (
                    <DataTable
                        value={
                            genericLogData?.content &&
                            genericLogData?.content?.length > 0
                                ? [...genericLogData?.content]
                                : []
                        }
                        scrollable
                        rowHover
                        emptyMessage={translateCommons('No results found')}
                        columnResizeMode="fit"
                        reorderableColumns
                        scrollHeight="auto"
                        expandableRowGroups={
                            filterRequest.value?.type === 'onWristLog'
                        }
                        lazy
                        header={
                            <div className="col-12 flex justify-content-end">
                                <div className="w-15rem px-0">
                                    <ExportForm
                                        isAuditLog
                                        dataList={
                                            filterRequest.value?.type &&
                                            genericLogData?.content &&
                                            genericLogData?.content?.length > 0
                                                ? genericLogData?.content?.map(
                                                      item => {
                                                          return {
                                                              ...item[
                                                                  filterRequest
                                                                      .value
                                                                      ?.type
                                                              ],
                                                              modifiedBy:
                                                                  item?.modifiedBy,
                                                              createdAt:
                                                                  item?.createdAt,
                                                          }
                                                      },
                                                  )
                                                : []
                                        }
                                        advancedOptions={exportCol}
                                        columnList={exportCol}
                                        auditLog={true}
                                    />
                                </div>
                            </div>
                        }
                        responsiveLayout="scroll">
                        {renderColumns()}
                    </DataTable>
                )}

                <PaginatorTemplate
                    currentRecords={genericLogData?.content?.length ?? 0}
                    totalRecords={
                        genericLogData && genericLogData?.totalElements
                            ? genericLogData?.totalElements
                            : 0
                    }
                    totalPages={genericLogData?.totalPages}
                    currentPage={pageable.value?.pageNumber ?? 0}
                    currentPageSize={pageable.value?.rows}
                    onPageSizeChange={rows =>
                        (pageable.value = {...pageable.value, rows: rows})
                    }
                    onPageChange={number =>
                        (pageable.value = {
                            ...pageable.value,
                            pageNumber: number,
                        })
                    }
                />
            </React.Fragment>
        )
    }
    //#endregion

    return (
        <React.Fragment>
            {!toolboxState.value ? (
                <Dialog
                    visible={visible.value}
                    header={
                        <div className="col-12">
                            <h5 className="mb-0 font-bold">
                                <strong>
                                    {translateDatatable(
                                        (filterRequest.value?.type === 'userLog'
                                            ? 'User '
                                            : filterRequest.value?.type ===
                                              'parameterLog'
                                            ? 'Parameter '
                                            : filterRequest.value?.type ===
                                              'hcpRoleLog'
                                            ? "Hcp's Role "
                                            : filterRequest.value?.type ===
                                              'watchNotificationLog'
                                            ? 'Watch Notification '
                                            : filterRequest.value?.type ===
                                              'biometricsLog'
                                            ? 'Biometrics '
                                            : filterRequest.value?.type ===
                                              'dischargeLog'
                                            ? 'Discharge Information '
                                            : filterRequest.value?.type ===
                                              'importLog'
                                            ? 'Import Logs '
                                            : filterRequest.value?.type ===
                                              'exportLog'
                                            ? 'Export Logs '
                                            : filterRequest.value?.type ===
                                              'watchConfigLog'
                                            ? 'Watch Configuration '
                                            : filterRequest.value?.type ===
                                              'onWristLog'
                                            ? 'Device Activities '
                                            : filterRequest.value?.type ===
                                              'batteryChargeLog'
                                            ? 'Battery Charge '
                                            : '') + 'Audit Log',
                                    )}
                                </strong>
                            </h5>
                        </div>
                    }
                    footer={
                        <div className="col-12">
                            <Button
                                className="w-full h-3rem"
                                icon="pi pi-times"
                                label={translateCommons('CLOSE')}
                                onClick={handleHideForm}
                            />
                        </div>
                    }
                    closable
                    style={{width: '80vw'}}
                    onHide={handleHideForm}>
                    {renderTable()}
                </Dialog>
            ) : (
                <div className="w-full">
                    <motion.div
                        initial={{x: 300}}
                        animate={{x: 0}}
                        transition={{type: 'spring'}}>
                        {renderTable()}
                    </motion.div>
                </div>
            )}
        </React.Fragment>
    )
})

export default GenericLogTable
